import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useAuth } from '../../hooks/useAuth';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../components/ui/use-toast';
import { cn } from '../../lib/utils';

// icons
import { Eye, EyeSlash, ArrowLeft } from 'iconsax-react';

// project imports
import { createTenant } from '../../services/tenantApi';

// validation schema
const validationSchema = Yup.object({
  organizationName: Yup.string().required('Organization name is required'),
  adminName: Yup.string().required('Admin name is required'),
  adminEmail: Yup.string().email('Invalid email').required('Admin email is required'),
  adminPassword: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  phone: Yup.string().matches(/^\+91[6-9]\d{9}$/, 'Phone number must be in format +91XXXXXXXXXX').required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  domain: Yup.string().required('Domain name is required')
});

const steps = ['Basic Info', 'Admin Details', 'Contact Info'];

// ==============================|| CREATE TENANT ||============================== //

const CreateTenant = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  if (!hasPermission('client.create')) {
    return <Navigate to="/tenants" replace />;
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError('');
      await createTenant(values);
      toast({
        title: "Success",
        description: "Healthcare agency onboarding completed successfully!",
      });
      navigate('/tenants');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create tenant');
      toast({
        title: "Registration Failed",
        description: err.response?.data?.message || 'Failed to create tenant',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step, values, errors) => {
    const stepFields = {
      0: ['organizationName', 'domain'],
      1: ['adminName', 'adminEmail', 'adminPassword'],
      2: ['phone', 'address']
    };
    
    const requiredFields = stepFields[step];
    const missingFields = requiredFields.filter(field => !values[field] || errors[field]);
    
    if (missingFields.length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields before proceeding",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const handleNext = (values, errors) => {
    if (validateStep(activeStep, values, errors)) {
      setActiveStep(activeStep + 1);
    }
  };

  const renderStepContent = (step, values, errors, touched, handleChange, handleBlur) => {
    switch (step) {
      case 0:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in-50 duration-300">
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">
                Organization Name <span className="text-rose-500">*</span>
              </label>
              <Input
                name="organizationName"
                value={values.organizationName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. Apex Healthcare Group"
                className={touched.organizationName && errors.organizationName ? "border-rose-500 focus-visible:ring-rose-500" : ""}
              />
              {touched.organizationName && errors.organizationName && (
                <p className="text-xs text-rose-500 mt-1">{errors.organizationName}</p>
              )}
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">
                Custom Domain <span className="text-rose-500">*</span>
              </label>
              <div className={cn(
                "flex rounded-md border bg-background focus-within:ring-2 focus-within:ring-offset-2 overflow-hidden transition-all duration-150",
                touched.domain && errors.domain ? "border-rose-500 focus-within:ring-rose-500" : "border-input focus-within:ring-ring"
              )}>
                <input
                  name="domain"
                  value={values.domain}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="domain-name"
                  className="flex-1 h-10 px-3 py-2 text-sm bg-transparent outline-none border-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex items-center justify-center bg-muted text-muted-foreground border-l px-3 text-sm font-semibold select-none">
                  .optihhcp.com
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">This domain will be used to access the agency's portals.</p>
              {touched.domain && errors.domain && (
                <p className="text-xs text-rose-500 mt-1">{errors.domain}</p>
              )}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in-50 duration-300">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Admin Name <span className="text-rose-500">*</span>
              </label>
              <Input
                name="adminName"
                value={values.adminName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="John Doe"
                className={touched.adminName && errors.adminName ? "border-rose-500 focus-visible:ring-rose-500" : ""}
              />
              {touched.adminName && errors.adminName && (
                <p className="text-xs text-rose-500 mt-1">{errors.adminName}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Admin Email <span className="text-rose-500">*</span>
              </label>
              <Input
                name="adminEmail"
                type="email"
                value={values.adminEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="admin@agency.com"
                className={touched.adminEmail && errors.adminEmail ? "border-rose-500 focus-visible:ring-rose-500" : ""}
              />
              {touched.adminEmail && errors.adminEmail && (
                <p className="text-xs text-rose-500 mt-1">{errors.adminEmail}</p>
              )}
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">
                Admin Password <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Input
                  name="adminPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={values.adminPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Min. 6 characters"
                  className={touched.adminPassword && errors.adminPassword ? "border-rose-500 focus-visible:ring-rose-500 pr-10" : "pr-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {touched.adminPassword && errors.adminPassword && (
                <p className="text-xs text-rose-500 mt-1">{errors.adminPassword}</p>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in-50 duration-300">
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">
                Phone Number <span className="text-rose-500">*</span>
              </label>
              <Input
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="+919876543210"
                className={touched.phone && errors.phone ? "border-rose-500 focus-visible:ring-rose-500" : ""}
              />
              <p className="text-xs text-muted-foreground">Format: +91XXXXXXXXXX (e.g. +919876543210)</p>
              {touched.phone && errors.phone && (
                <p className="text-xs text-rose-500 mt-1">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-sm font-medium text-foreground">
                Address <span className="text-rose-500">*</span>
              </label>
              <Textarea
                name="address"
                rows={3}
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter organization contact address..."
                className={touched.address && errors.address ? "border-rose-500 focus-visible:ring-rose-500 min-h-[100px]" : "min-h-[100px]"}
              />
              {touched.address && errors.address && (
                <p className="text-xs text-rose-500 mt-1">{errors.address}</p>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header section with Back Button - Left-Aligned */}
      <div className="flex flex-col gap-1.5 px-1 text-left">
        <button
          onClick={() => navigate('/tenants')}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-fit focus:outline-none"
        >
          <ArrowLeft size={16} />
          Back to Agencies
        </button>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Create New Healthcare Agency
        </h1>
      </div>

      <Card className="border border-border shadow-md rounded-xl overflow-hidden">
        {/* Custom Modern Line-based Stepper Header Banner */}
        <div className="bg-muted/30 px-8 py-4 border-b">
          <div className="grid grid-cols-3 gap-4">
            {steps.map((label, index) => {
              const isCompleted = index < activeStep;
              const isActive = index === activeStep;
              return (
                <div key={label} className="space-y-2 text-left">
                  <div className={`h-1.5 rounded-full transition-all duration-300 ${
                    isCompleted ? 'bg-primary' : 
                    isActive ? 'bg-primary/40' : 
                    'bg-muted'
                  }`} />
                  <span className={`text-[10px] sm:text-xs font-semibold block transition-colors duration-300 ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    Step {index + 1}: {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <CardContent className="p-8">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-rose-800 text-sm mb-6 animate-in fade-in-50">
              {error}
            </div>
          )}

          <Formik
            initialValues={{
              organizationName: '',
              domain: '',
              adminName: '',
              adminEmail: '',
              adminPassword: '',
              phone: '+91',
              address: ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, isValid }) => (
              <Form className="space-y-6">
                {renderStepContent(activeStep, values, errors, touched, handleChange, handleBlur)}

                <div className="flex justify-between items-center border-t pt-6 mt-8">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/tenants')}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </Button>
                  
                  <div className="flex items-center gap-3">
                    {activeStep > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setActiveStep(activeStep - 1)}
                        className="hover:bg-muted"
                      >
                        Back
                      </Button>
                    )}
                    
                    {activeStep === steps.length - 1 ? (
                      <Button
                        type="submit"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6"
                        disabled={loading || !isValid}
                      >
                        {loading ? 'Creating...' : 'Create Agency'}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6"
                        onClick={() => handleNext(values, errors)}
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTenant;